import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import FileTree from "files/components/FileTree";
import FileType from "files/types/enums/fileType";
import FileInfo from "files/types/interfaces/fileInfo";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";

interface FileSelectShadcnProps {
  selectedFile: FileInfo | null;
  setSelectedFile: Dispatch<SetStateAction<FileInfo | null>>;
  onSelectFile: (file: FileInfo) => void;
  maxHeight?: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingPlaceholder?: string;
}

/**
 * Inline file picker wrapping MUI FileTree with shadcn/ui styling.
 * Shows a read-only display of the selected file path above an always-visible file browser.
 */
const FileSelectShadcn = ({
  selectedFile,
  setSelectedFile,
  onSelectFile,
  placeholder = "Select source file...",
  className,
  disabled = false,
  isLoading = false,
  loadingPlaceholder = "Loading file information...",
}: FileSelectShadcnProps) => {
  const fileKeyOnMountRef = useRef<string | null>(selectedFile?.key ?? null);

  useEffect(() => {
    if (selectedFile?.type === FileType.FILE && selectedFile.key !== fileKeyOnMountRef.current) {
      onSelectFile(selectedFile);
      fileKeyOnMountRef.current = selectedFile.key;
    }
  }, [selectedFile, selectedFile?.key, onSelectFile]);

  return (
    <div className={cn("w-full", className)}>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {loadingPlaceholder}
        </div>
      ) : disabled ? (
        <div className="text-sm text-muted-foreground py-2 opacity-50">{placeholder}</div>
      ) : (
        <div className="rounded-lg border bg-white p-4">
          <FileTree
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            showFiles
            maxHeight={300}
          />
        </div>
      )}
    </div>
  );
};

export default FileSelectShadcn;
